<template>
  <div class="h-screen flex items-center justify-center p-4 bg-dark-secondary">
    <div class="w-full max-w-md">

      <!-- Logo -->
      <div class="flex flex-col items-center mb-8">
        <div class="w-12 h-12 bg-accent rounded-xl flex items-center justify-center shadow-lg mb-3">
          <Shield class="w-6 h-6 text-white" />
        </div>
        <h1 class="text-2xl font-bold text-dark-primary tracking-tight">SeguraWatch</h1>
        <p class="text-sm text-dark-secondary mt-1">PAM Session Intelligence</p>
      </div>

      <!-- Step: request -->
      <div v-if="step === 'request'" class="bg-dark-primary border border-dark rounded-2xl p-8 shadow-xl">
        <h2 class="text-lg font-bold text-dark-primary mb-1">Solicitar acesso</h2>
        <p class="text-sm text-dark-secondary mb-6">
          Sua conta Google foi autenticada, mas ainda não há um tenant associado a ela.
          Informe o e-mail corporativo para solicitar o provisionamento do seu ambiente.
        </p>

        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-dark-secondary uppercase tracking-wide mb-1.5">
              E-mail corporativo
            </label>
            <input
              v-model="email"
              type="email"
              placeholder="voce@empresa.com"
              class="onboarding-input"
              :disabled="loading"
              @keydown.enter="submitRequest"
            />
            <p v-if="emailError" class="text-xs text-error mt-1.5">{{ emailError }}</p>
          </div>

          <button
            @click="submitRequest"
            :disabled="loading || !email"
            class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-accent hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
            <span>{{ loading ? 'Enviando solicitação…' : 'Solicitar acesso' }}</span>
          </button>

          <p v-if="errorMsg" class="text-xs text-error text-center">{{ errorMsg }}</p>
        </div>

        <div class="mt-6 pt-6 border-t border-dark flex justify-center">
          <button @click="handleLogout" class="text-xs text-dark-secondary hover:text-dark-primary transition">
            Sair da conta Google
          </button>
        </div>
      </div>

      <!-- Step: pending -->
      <div v-else-if="step === 'pending'" class="bg-dark-primary border border-dark rounded-2xl p-8 shadow-xl text-center">
        <div class="w-14 h-14 rounded-full bg-warning/10 border border-warning/30 flex items-center justify-center mx-auto mb-4">
          <Clock class="w-7 h-7 text-warning" />
        </div>
        <h2 class="text-lg font-bold text-dark-primary mb-2">Solicitação enviada</h2>
        <p class="text-sm text-dark-secondary mb-1">
          Seu slug: <span class="font-mono text-accent font-semibold">{{ slug }}</span>
        </p>
        <p class="text-sm text-dark-secondary mt-3">
          Nossa equipe irá provisionar seu ambiente e enviar as credenciais de acesso por e-mail.
          Assim que receber, acesse as <strong class="text-dark-primary">Configurações</strong> para ativar o backend.
        </p>

        <div class="mt-6 pt-6 border-t border-dark">
          <button @click="handleLogout" class="text-xs text-dark-secondary hover:text-dark-primary transition">
            Sair da conta Google
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Shield, Clock, Loader2 } from "lucide-vue-next";
import { logoutUser, createTenantInFirestore } from "../services/authService";
import { useAuthStore } from "../stores/authStore";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const router    = useRouter();

const step       = ref("request");
const email      = ref(authStore.user?.email ?? "");
const emailError = ref("");
const errorMsg   = ref("");
const loading    = ref(false);
const slug       = ref("");

const TENANT_REQUEST_URL = import.meta.env.DEV 
  ? "/watchctl-proxy/tenantrequest" 
  : "https://watchctl.seguraw.ai/tenantrequest";

async function submitRequest() {
  emailError.value = "";
  errorMsg.value   = "";

  if (!email.value || !email.value.includes("@")) {
    emailError.value = "Informe um e-mail válido.";
    return;
  }

  loading.value = true;
  try {
    const res = await fetch(TENANT_REQUEST_URL, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ email: email.value }),
    });

    if (res.status === 422) {
      emailError.value = "E-mail inválido.";
      return;
    }
    if (!res.ok) {
      errorMsg.value = `Erro ao enviar solicitação (${res.status}). Tente novamente.`;
      return;
    }

    const data = await res.json();
    slug.value  = data.slug;

    // Create tenant in Firestore with pending status
    await createTenantInFirestore(authStore.user.uid, email.value, data.slug);

    // Update store
    authStore.setTenant({
      tenantId:     data.slug,
      role:         "admin",
      tenantStatus: "pending",
      apiBase:      null,
      apiKey:       null,
    });

    step.value = "pending";
  } catch (err) {
    console.error("[Onboarding] Request failed:", err);
    errorMsg.value = "Erro de conexão. Verifique sua internet e tente novamente.";
  } finally {
    loading.value = false;
  }
}

async function handleLogout() {
  await logoutUser();
  authStore.clearUser();
  router.push("/login");
}
</script>

<style scoped>
.onboarding-input {
  width: 100%;
  height: 2.5rem;
  padding: 0.625rem 1rem;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;
}
.onboarding-input:focus {
  border-color: var(--accent);
}
.onboarding-input::placeholder {
  color: var(--text-secondary);
}
.onboarding-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>